import { addWeeks, parseJSON } from 'date-fns';
import { startCase } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnCurrentOrganization } from '@lib/auth';
import { GOOGLE_MAP_POPUP_DATA } from '@lib/geo/google/map';
import { t } from '@lib/translation';

import { LprAggregationsAPI, FsLprCamera, LprCameraModel } from '../../api';
import { SearchReasonDialogComponent } from '../../components/search-reason-dialog/search-reason-dialog.component';
import { LprCameraStatsDialogComponent } from '../lpr-camera-stats-dialog/lpr-camera-stats-dialog.component';
import { LprQueryCameraFormComponent } from '../lpr-query-camera-form/lpr-query-camera-form.component';
import { FsLprCameraAdvancedMarker, FsLprQueryCameraAdvancedMarker } from '../map';
import { RecentHitDialogComponent } from '../recent-hits-dialog/recent-hit-dialog.component';

@Component({
  selector: 'fs-lpr-camera-details-popup',
  templateUrl: './lpr-camera-details-popup.component.html',
  styleUrls: ['./lpr-camera-details-popup.component.scss'],
})
export class LprCameraDetailsPopupComponent implements OnDestroy, OnInit {
  get markerUpdated$(): Observable<any> {
    return this._markerUpdated$.asObservable();
  }

  public canEditLpr: boolean;

  private readonly _markerUpdated$ = new Subject();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(GOOGLE_MAP_POPUP_DATA)
    public data: FsLprCameraAdvancedMarker | FsLprQueryCameraAdvancedMarker,
    public readonly router: Router,
    private readonly _matDialog: MatDialog,
    private readonly _lprAggregationsAPI: LprAggregationsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _ability: SnAbilityService,
  ) {
    this.canEditLpr = this._ability.can({
      subject: 'lpr/lprpermissions',
      action: 'change_lprcamera',
    });
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public toStartCase(key: string): string {
    if (key.toLowerCase() === 'up') {
      return t('common.status');
    }
    return startCase(key);
  }

  public exclude(key: string): boolean {
    return (
      ['position', 'integrationType', 'integrationId', 'editable', 'id', 'cameraId', 'pk'].indexOf(
        key.toLowerCase(),
      ) > -1
    );
  }

  public formatDate(data: Date | string): Date {
    return parseJSON(data);
  }

  public editHeading(camera: LprCameraModel): void {
    const dialogRef = this._matDialog.open(LprQueryCameraFormComponent, {
      data: camera,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          const properties = new LprCameraModel(res);
          this.data.camera.properties = {
            ...this.data.camera.properties,
            name: properties.name,
            heading: properties.heading,
          };
          this._markerUpdated$.next(res);
          this._cd.detectChanges();
        }
      });
  }

  public viewStats(camera: any): void {
    const mode = 'day';
    let timeStart = new Date();
    timeStart = addWeeks(timeStart, -2);
    const formattedTimeStart = timeStart ? Math.floor(timeStart.getTime() / 1000) : null;
    const timeEnd = new Date();
    const formattedTimeEnd = timeEnd ? Math.floor(timeEnd.getTime() / 1000) : null;
    this._lprAggregationsAPI
      .getLprResultsPerCamera(camera.id, mode, formattedTimeEnd, formattedTimeStart)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this._matDialog.open(LprCameraStatsDialogComponent, {
          data: {
            cameraName: camera.id,
            results: res,
          },
          width: '75vw',
        });
      });
  }

  public openRecentHits(lprCamera: FsLprCamera): void {
    const dialogRef = this._matDialog.open(RecentHitDialogComponent, {
      data: {
        cameraName: lprCamera.id,
        type: lprCamera.type,
        geometry: lprCamera.position,
        cameras: [lprCamera.integrationId],
      },
      width: '600px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        return result;
      });
  }

  public searchPlateReads(cameraName: string): void {
    const dialogRef = this._matDialog.open(SearchReasonDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          this.router.navigate(['/plates/list/queries'], {
            queryParams: {
              cameras: cameraName,
              search_reason: res,
            },
          });
        }
      });
  }
}
